/**
 * MIRROR von src/core/money.ts:formatGermanDate — bei BE-Edit
 * Drift-Test (src/lib/date-format-frontend-mirror-drift.test.ts) re-running.
 *
 * Bewusst keine Date-Konstruktion bei `formatGermanDate`, damit Timezone-
 * Edge-Cases (UTC-Offset, DST) keine Datums-Sprünge erzeugen. Erwartet
 * exakt YYYY-MM-DD (DB-DATE-Spalten-Format).
 */
export function formatGermanDate(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    throw new Error(`formatGermanDate: erwartet YYYY-MM-DD, bekam "${isoDate}"`);
  }
  const [yyyy, mm, dd] = isoDate.split('-');
  return `${dd}.${mm}.${yyyy}`;
}

/**
 * Für ISO-Timestamps (BE liefert `created_at` o.ae. als ISO-String).
 * Format: dd.MM.yyyy, HH:mm. Timezone fix Europe/Berlin (nicht User-Browser),
 * damit Anzeige unabhaengig vom Client-Standort konsistent ist.
 */
export function formatGermanDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`formatGermanDateTime: ungültiges ISO-Datum "${iso}"`);
  }
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Für ISO-Timestamps wenn nur das Datum (ohne Zeit) angezeigt werden soll.
 * Format: dd.MM.yyyy. Timezone fix Europe/Berlin. Anders als
 * `formatGermanDate` akzeptiert dieser Helper Timestamps mit Zeit-Anteil
 * (z.B. `created_at`, `versendet_am`).
 */
export function formatGermanDateFromTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`formatGermanDateFromTimestamp: ungültiges ISO-Datum "${iso}"`);
  }
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Tage zwischen Heute und einem Ziel-Datum (ISO-Timestamp oder YYYY-MM-DD).
 * Negativ wenn Ziel in Vergangenheit.
 */
export function daysUntil(iso: string): number {
  const ms = msUntil(iso);
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Millisekunden zwischen jetzt und einem Ziel-Datum. Negativ wenn Ziel in
 * Vergangenheit. Konsolidiert das `new Date(x).getTime() - Date.now()`-
 * Pattern (SLA-Reaktionszeit-Anzeige in Sub-Tag-Auflösung).
 */
export function msUntil(iso: string): number {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) {
    throw new Error(`msUntil: ungültiges Datum "${iso}"`);
  }
  return target - Date.now();
}
